import { Action, ActionSubject, type ScreenEvent, type UIData, type UIEvent } from '../new-analytics-types';

const ACTIONS_DROPDOWN_NAME = 'configDrivenTableRowActionsDropdown';
const FILTER_ACTION_SUBJECT_ID = 'configDrivenTableFilter';
const HEADER_ACTION_SUBJECT_ID = 'configDrivenTableHeader';
const ROW_ACTION_SUBJECT_ID = 'configDrivenTableRow';

export const configDrivenTableEvents = {
  screen: {
    actionsDropdownViewed: (): ScreenEvent => ({
      data: {
        name: ACTIONS_DROPDOWN_NAME,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86076',
    }),
  },
  ui: {
    filterResetClick: (source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: FILTER_ACTION_SUBJECT_ID,
        actionSubject: ActionSubject.Button,
        source,
        attributes: {
          actionId: 'resetFilters',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86106',
    }),
    filterReloadClick: (source: string): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: FILTER_ACTION_SUBJECT_ID,
        actionSubject: ActionSubject.Button,
        source,
        attributes: {
          actionId: 'reloadData',
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86106',
    }),
    filterSearchTextChange: (source: string, attributes: { searchText: string }): UIEvent => ({
      data: {
        action: Action.Searched,
        actionSubjectId: FILTER_ACTION_SUBJECT_ID,
        actionSubject: ActionSubject.Field,
        source,
        attributes: {
          searchText: attributes.searchText,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86102',
    }),
    filterOptionChange: (source: string, attributes: { filterId: string; value: string }): UIEvent => ({
      data: {
        action: Action.Changed,
        actionSubjectId: FILTER_ACTION_SUBJECT_ID,
        actionSubject: ActionSubject.Option,
        source,
        attributes: {
          filterId: attributes.filterId,
          value: attributes.value,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86105',
    }),
    paginationPageSizeChange: (source: string, attributes: { pageSize: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'configDrivenTablePaginationPageSize',
        actionSubject: ActionSubject.DropListItem,
        source,
        attributes: { pageSize: attributes.pageSize },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86110',
    }),
    paginationNavigationChange: (
      source: string,
      attributes: { direction: 'next' | 'previous'; startIndex: number; endIndex: number; pageSize: number },
    ): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'configDrivenTablePaginationNavigation',
        actionSubject: ActionSubject.Button,
        source,
        attributes: {
          direction: attributes.direction,
          startIndex: attributes.startIndex,
          endIndex: attributes.endIndex,
          pageSize: attributes.pageSize,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86111',
    }),
    headerSortClick: (source: string, attributes: { columnId: string; direction: 'asc' | 'desc' }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: HEADER_ACTION_SUBJECT_ID,
        actionSubject: ActionSubject.Button,
        source,
        attributes: { columnId: attributes.columnId, direction: attributes.direction },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86107',
    }),
    headerTooltipHover: ({ source, analyticsData }: { source: string; analyticsData?: Partial<UIData> }): UIEvent => ({
      data: {
        action: Action.Hovered,
        actionSubjectId: HEADER_ACTION_SUBJECT_ID,
        actionSubject: ActionSubject.Tooltip,
        source,
        attributes: { ...analyticsData?.attributes, tooltipId: analyticsData?.attributes?.tooltipId },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86109',
    }),
    rowActionClick: ({ source, analyticsData }: { source: string; analyticsData?: Partial<UIData> }): UIEvent => ({
      data: {
        ...analyticsData,
        action: Action.Clicked,
        actionSubjectId: ROW_ACTION_SUBJECT_ID,
        actionSubject: ActionSubject.Button,
        source,
        attributes: {
          ...analyticsData?.attributes,
          actionId: analyticsData?.attributes?.actionId,
          position: analyticsData?.attributes?.position,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86041',
    }),
    rowPopupClick: ({ source, analyticsData }: { source: string; analyticsData?: Partial<UIData> }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: ROW_ACTION_SUBJECT_ID,
        actionSubject: ActionSubject.DropListTrigger,
        source,
        attributes: { ...analyticsData?.attributes, popupId: analyticsData?.attributes?.popupId },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86043',
    }),
    rowLinkClick: ({ source, analyticsData }: { source: string; analyticsData?: Partial<UIData> }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: ROW_ACTION_SUBJECT_ID,
        actionSubject: ActionSubject.Link,
        source,
        attributes: {
          ...analyticsData?.attributes,
          linkId: analyticsData?.attributes?.linkId,
          url: analyticsData?.attributes?.url,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86053',
    }),
  },
};
